import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Eye, X, Send, XCircle, CheckCircle2, History } from 'lucide-react';
import { toast } from 'sonner';
import { RichTextEditor } from '../../components/admin/RichTextEditor';
import { ListFieldEditor } from '../../components/admin/ListFieldEditor';
import { JobPositionView } from '../../components/careers/JobPositionView';
import {
  careersService, JobPositionAdminDetail, JobPositionPayload, JobStatus, STATUS_LABELS,
  CONTRACT_LABELS, SENIORITY_LABELS, DEPARTMENT_LABELS, REMOTE_LABELS,
  ContractType, Seniority, Department, RemotePolicy, SalaryPeriod,
} from '../../services/careersService';

const EMPTY: JobPositionPayload = {
  titolo: '', sede: '', tipoContratto: 'tempo_indeterminato', area: 'commerciale',
  seniority: 'middle', descrizione: '', requisiti: [], slug: '', riferimento: '',
  sedeRemoto: 'in_sede', requisitiPreferenziali: [], offriamo: [], retribuzione: null,
  dataPubblicazione: null, dataChiusura: null, dataScadenzaCandidature: null,
  emailCandidature: '', posizioniDisponibili: 1,
};

const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-medium block mb-1">{label}{required && ' *'}</label>
    {children}
  </div>
);

const inputCls = 'w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]';

export default function AdminCareerDetailPage() {
  const { posId } = useParams();
  const navigate = useNavigate();
  const isNew = !posId || posId === 'nuova';

  const [form, setForm] = useState<JobPositionPayload>(EMPTY);
  const [doc, setDoc] = useState<JobPositionAdminDetail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [hasSalary, setHasSalary] = useState(false);

  const hydrate = (d: JobPositionAdminDetail) => {
    setDoc(d);
    setHasSalary(!!d.retribuzione);
    setForm({
      titolo: d.titolo, sede: d.sede, tipoContratto: d.tipoContratto, area: d.area,
      seniority: d.seniority, descrizione: d.descrizione, requisiti: d.requisiti,
      slug: d.slug, riferimento: d.riferimento || '', sedeRemoto: d.sedeRemoto,
      requisitiPreferenziali: d.requisitiPreferenziali, offriamo: d.offriamo,
      retribuzione: d.retribuzione || null, dataPubblicazione: d.dataPubblicazione || null,
      dataChiusura: d.dataChiusura || null, dataScadenzaCandidature: d.dataScadenzaCandidature || null,
      emailCandidature: d.emailCandidature === 'info@dtksrl.eu' ? '' : d.emailCandidature,
      posizioniDisponibili: d.posizioniDisponibili,
    });
  };

  useEffect(() => {
    if (isNew) return;
    careersService.adminGet(posId!)
      .then(hydrate)
      .catch(() => toast.error('Posizione non trovata'))
      .finally(() => setLoading(false));
  }, [posId, isNew]);

  const set = <K extends keyof JobPositionPayload>(k: K, v: JobPositionPayload[K]) => setForm(f => ({ ...f, [k]: v }));

  const payload = (): JobPositionPayload => ({
    ...form,
    riferimento: form.riferimento?.trim() || null,
    emailCandidature: form.emailCandidature?.trim() || null,
    retribuzione: hasSalary && form.retribuzione?.min ? { ...form.retribuzione, valuta: 'EUR' } : null,
  });

  const validate = (forPublish: boolean): string | null => {
    if (form.titolo.trim().length < 3) return 'Il titolo è obbligatorio (min. 3 caratteri)';
    if (form.sede.trim().length < 2) return 'La sede è obbligatoria';
    const plain = form.descrizione.replace(/<[^>]+>/g, '').trim();
    if (!plain) return 'La descrizione è obbligatoria';
    if (forPublish && form.requisiti.length === 0) return 'Inserisci almeno un requisito per pubblicare';
    return null;
  };

  const save = async (opts: { silentHistory?: boolean } = {}): Promise<JobPositionAdminDetail | null> => {
    const err = validate(false);
    if (err) { toast.error(err); return null; }
    setSaving(true);
    try {
      const saved = isNew
        ? await careersService.adminCreate(payload())
        : await careersService.adminUpdate(posId!, payload(), opts.silentHistory);
      // Risincronizza il form con la risposta del server: date e slug sono
      // calcolati lato backend e non devono essere azzerati dal PUT successivo.
      hydrate(saved);
      if (!opts.silentHistory) toast.success(isNew ? 'Posizione creata come bozza' : 'Modifiche salvate');
      if (isNew) navigate(`/admin/carriere/${saved.id}`, { replace: true });
      return saved;
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore durante il salvataggio');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const changeStato = async (stato: JobStatus) => {
    if (stato === 'aperta') {
      const err = validate(true);
      if (err) { toast.error(err); return; }
    }
    // Il salvataggio è solo il pre-step del cambio stato: non deve lasciare
    // una voce "modificata" nello storico.
    const saved = await save({ silentHistory: true });
    if (!saved) return;
    try {
      const out = await careersService.adminSetStato(saved.id, stato);
      hydrate(out);
      toast.success(stato === 'aperta' ? 'Posizione pubblicata' : stato === 'chiusa' ? 'Posizione chiusa' : 'Riportata in bozza');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Errore nel cambio di stato');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento posizione...
      </div>
    );
  }

  const stato = doc?.stato || 'bozza';

  return (
    <div className="p-4 sm:p-6 max-w-5xl" data-testid="admin-career-detail-page">
      <button onClick={() => navigate('/admin/carriere')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="career-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Torna all&apos;elenco
      </button>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{isNew ? 'Nuova posizione' : form.titolo || 'Posizione'}</h1>
          <div className="text-sm text-muted-foreground mt-0.5">
            Stato: <span className="font-medium text-foreground" data-testid="career-current-stato">{STATUS_LABELS[stato]}</span>
            {doc && <> · Candidature avviate: <span className="font-medium text-foreground">{doc.clickCandidature}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setPreview(true)} className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 hover:bg-secondary/60 transition-colors" data-testid="career-preview-btn">
            <Eye className="w-4 h-4" /> Anteprima
          </button>
          <button onClick={() => save()} disabled={saving} className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 hover:bg-secondary/60 transition-colors disabled:opacity-50" data-testid="career-save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva bozza
          </button>
          {stato !== 'aperta' && (
            <button onClick={() => changeStato('aperta')} disabled={saving} className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)] transition-colors disabled:opacity-50" data-testid="career-publish-btn">
              {stato === 'chiusa' ? <><CheckCircle2 className="w-4 h-4" /> Riapri</> : <><Send className="w-4 h-4" /> Pubblica</>}
            </button>
          )}
          {stato === 'aperta' && (
            <button onClick={() => changeStato('chiusa')} disabled={saving} className="h-9 px-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium flex items-center gap-1.5 hover:bg-amber-100 transition-colors" data-testid="career-close-btn">
              <XCircle className="w-4 h-4" /> Chiudi posizione
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        {/* Dati principali */}
        <div className="rounded-xl border bg-white p-4 space-y-3">
          <h2 className="text-sm font-semibold">Dati principali</h2>
          <Field label="Titolo" required>
            <input value={form.titolo} onChange={e => set('titolo', e.target.value)} className={inputCls} data-testid="career-field-titolo" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sede" required>
              <input value={form.sede} onChange={e => set('sede', e.target.value)} placeholder="Roma (RM)" className={inputCls} data-testid="career-field-sede" />
            </Field>
            <Field label="Modalità di lavoro">
              <select value={form.sedeRemoto} onChange={e => set('sedeRemoto', e.target.value as RemotePolicy)} className={inputCls} data-testid="career-field-remoto">
                {Object.entries(REMOTE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo di contratto" required>
              <select value={form.tipoContratto} onChange={e => set('tipoContratto', e.target.value as ContractType)} className={inputCls} data-testid="career-field-contratto">
                {Object.entries(CONTRACT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Area / dipartimento" required>
              <select value={form.area} onChange={e => set('area', e.target.value as Department)} className={inputCls} data-testid="career-field-area">
                {Object.entries(DEPARTMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Seniority" required>
              <select value={form.seniority} onChange={e => set('seniority', e.target.value as Seniority)} className={inputCls} data-testid="career-field-seniority">
                {Object.entries(SENIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Riferimento">
              <input value={form.riferimento || ''} onChange={e => set('riferimento', e.target.value)} placeholder="COM-2026-01" className={inputCls} data-testid="career-field-riferimento" />
            </Field>
            <Field label="N. posizioni">
              <input type="number" min={1} max={99} value={form.posizioniDisponibili} onChange={e => set('posizioniDisponibili', Number(e.target.value) || 1)} className={inputCls} data-testid="career-field-posizioni" />
            </Field>
          </div>
          <div className="rounded-lg border p-3">
            <label className="flex items-center gap-2 text-xs font-medium mb-2">
              <input type="checkbox" checked={hasSalary} onChange={e => {
                setHasSalary(e.target.checked);
                if (e.target.checked && !form.retribuzione) set('retribuzione', { min: 0, max: null, valuta: 'EUR', periodo: 'anno' });
              }} data-testid="career-field-has-salary" />
              Indica la retribuzione (mostrata pubblicamente)
            </label>
            {hasSalary && (
              <div className="grid grid-cols-3 gap-3">
                <Field label="Da (€)">
                  <input type="number" min={0} value={form.retribuzione?.min ?? ''} onChange={e => set('retribuzione', { ...(form.retribuzione || { valuta: 'EUR', periodo: 'anno' as SalaryPeriod, max: null }), min: Number(e.target.value) })} className={inputCls} data-testid="career-field-salary-min" />
                </Field>
                <Field label="A (€)">
                  <input type="number" min={0} value={form.retribuzione?.max ?? ''} onChange={e => set('retribuzione', { ...(form.retribuzione || { valuta: 'EUR', periodo: 'anno' as SalaryPeriod, min: 0 }), max: e.target.value ? Number(e.target.value) : null })} className={inputCls} data-testid="career-field-salary-max" />
                </Field>
                <Field label="Periodo">
                  <select value={form.retribuzione?.periodo || 'anno'} onChange={e => set('retribuzione', { ...(form.retribuzione || { valuta: 'EUR', min: 0, max: null }), periodo: e.target.value as SalaryPeriod })} className={inputCls} data-testid="career-field-salary-period">
                    <option value="anno">Anno</option>
                    <option value="mese">Mese</option>
                    <option value="ora">Ora</option>
                  </select>
                </Field>
              </div>
            )}
          </div>
        </div>

        {/* Pubblicazione */}
        <div className="rounded-xl border bg-white p-4 space-y-3 h-fit">
          <h2 className="text-sm font-semibold">Pubblicazione</h2>
          <Field label="Data pubblicazione">
            <input type="date" value={form.dataPubblicazione || ''} onChange={e => set('dataPubblicazione', e.target.value || null)} className={inputCls} data-testid="career-field-data-pub" />
          </Field>
          <Field label="Data chiusura">
            <input type="date" value={form.dataChiusura || ''} onChange={e => set('dataChiusura', e.target.value || null)} className={inputCls} data-testid="career-field-data-chiusura" />
          </Field>
          <Field label="Scadenza candidature">
            <input type="date" value={form.dataScadenzaCandidature || ''} onChange={e => set('dataScadenzaCandidature', e.target.value || null)} className={inputCls} data-testid="career-field-scadenza" />
          </Field>
          <Field label="Email candidature">
            <input value={form.emailCandidature || ''} onChange={e => set('emailCandidature', e.target.value)} placeholder="info@dtksrl.eu" className={inputCls} data-testid="career-field-email" />
          </Field>
          {!isNew && doc && (
            <Field label="Slug (URL pubblico)">
              <input value={form.slug || ''} onChange={e => set('slug', e.target.value)} disabled={stato !== 'bozza'} className={`${inputCls} disabled:bg-secondary/40`} data-testid="career-field-slug" />
            </Field>
          )}
        </div>
      </div>

      {/* Contenuti */}
      <div className="rounded-xl border bg-white p-4 mt-4 space-y-4">
        <h2 className="text-sm font-semibold">Contenuti</h2>
        <Field label="Descrizione del ruolo" required>
          <RichTextEditor value={form.descrizione} onChange={v => set('descrizione', v)} testId="career-field-descrizione" />
        </Field>
        <div className="flex flex-col gap-5">
          <ListFieldEditor
            label="Requisiti" required
            items={form.requisiti}
            onChange={v => set('requisiti', v)}
            placeholder="Es. Esperienza di almeno 3 anni nella vendita B2B"
            testId="career-field-requisiti"
          />
          <ListFieldEditor
            label="Requisiti preferenziali"
            items={form.requisitiPreferenziali}
            onChange={v => set('requisitiPreferenziali', v)}
            placeholder="Es. Conoscenza del mercato dei consumabili"
            testId="career-field-requisiti-pref"
          />
          <ListFieldEditor
            label="Cosa offriamo"
            items={form.offriamo}
            onChange={v => set('offriamo', v)}
            placeholder="Es. Auto aziendale, telefono e notebook"
            testId="career-field-offriamo"
          />
        </div>
      </div>

      {/* Storico */}
      {doc && doc.history.length > 0 && (
        <div className="rounded-xl border bg-white p-4 mt-4" data-testid="career-history">
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-3"><History className="w-4 h-4" /> Storico</h2>
          <div className="space-y-1.5">
            {[...doc.history].reverse().map((h, i) => (
              <div key={i} className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                <span className="font-mono">{new Date(h.at).toLocaleString('it-IT')}</span>
                <span>·</span>
                <span className="font-medium text-foreground">{h.byNome}</span>
                <span>·</span>
                <span className="capitalize">{h.azione}</span>
                {h.statoPrecedente && h.statoNuovo && <span>({h.statoPrecedente} → {h.statoNuovo})</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Anteprima */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="career-preview-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setPreview(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 p-6 sm:p-8">
            <button onClick={() => setPreview(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="career-preview-close">
              <X className="w-4 h-4" />
            </button>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">Anteprima pagina pubblica</div>
            <JobPositionView
              pos={{
                id: doc?.id || 'preview',
                slug: form.slug || 'anteprima',
                titolo: form.titolo || 'Titolo posizione',
                sede: form.sede,
                sedeRemoto: form.sedeRemoto,
                tipoContratto: form.tipoContratto,
                area: form.area,
                seniority: form.seniority,
                dataPubblicazione: form.dataPubblicazione,
                stato: stato,
                riferimento: form.riferimento || null,
                descrizione: form.descrizione,
                requisiti: form.requisiti,
                requisitiPreferenziali: form.requisitiPreferenziali,
                offriamo: form.offriamo,
                retribuzione: hasSalary ? form.retribuzione : null,
                dataChiusura: form.dataChiusura,
                dataScadenzaCandidature: form.dataScadenzaCandidature,
                emailCandidature: form.emailCandidature?.trim() || 'info@dtksrl.eu',
                posizioniDisponibili: form.posizioniDisponibili,
              }}
              showCta
            />
          </div>
        </div>
      )}
    </div>
  );
}
